import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import type { Token, LoginCredentials, RegisterData, User, Recording, RecordingListResponse, PasswordResetRequest, PasswordReset, PasswordResetResponse, Gender } from '../types'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

/**
 * Format error message from axios error
 */
function formatErrorMessage(error: any): string {
  // Network error (no response from server)
  if (!error.response) {
    if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
      return 'Verbindungsfehler: Server nicht erreichbar. Bitte prüfen Sie Ihre Internetverbindung.'
    }
    if (error.code === 'ECONNREFUSED') {
      return 'Verbindung abgelehnt: Backend-Server läuft möglicherweise nicht.'
    }
    if (error.message?.includes('CORS')) {
      return 'CORS-Fehler: Backend erlaubt keine Anfragen von diesem Origin.'
    }
    return `Netzwerkfehler: ${error.message || 'Unbekannter Fehler'}`
  }

  const status = error.response.status
  const data = error.response.data

  // Handle different HTTP status codes
  switch (status) {
    case 400:
      return data?.detail || 'Ungültige Anfrage. Bitte prüfen Sie Ihre Eingaben.'
    case 401:
      return 'Nicht autorisiert. Bitte melden Sie sich erneut an.'
    case 403:
      return data?.detail || 'Zugriff verweigert. Sie haben keine Berechtigung für diese Aktion.'
    case 404:
      return 'Ressource nicht gefunden.'
    case 422:
      // Validation errors
      if (Array.isArray(data?.detail)) {
        const errors = data.detail.map((e: any) => {
          if (e.loc && e.msg) {
            return `${e.loc.join('.')}: ${e.msg}`
          }
          return e.msg || e.message || JSON.stringify(e)
        })
        return errors.join(', ')
      }
      return data?.detail || 'Validierungsfehler. Bitte prüfen Sie Ihre Eingaben.'
    case 500:
      return 'Serverfehler. Bitte versuchen Sie es später erneut.'
    case 503:
      return 'Service nicht verfügbar. Bitte versuchen Sie es später erneut.'
    default:
      return data?.detail || `Fehler ${status}: ${data?.message || 'Unbekannter Fehler'}`
  }
}

class ApiClient {
  private client: AxiosInstance

  constructor() {
    this.client = axios.create({
      baseURL: `${API_URL}/api/v1`,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 30000, // 30 seconds timeout
    })

    // Add auth token to requests
    this.client.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const token = localStorage.getItem('access_token')
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`
        }
        
        // Wenn FormData gesendet wird, Content-Type entfernen (Browser setzt automatisch multipart/form-data mit Boundary)
        if (config.data instanceof FormData && config.headers) {
          delete config.headers['Content-Type']
        }
        
        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    // Handle response errors
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        // Format error message
        const formattedError = new Error(formatErrorMessage(error))
        
        // Handle 401 errors (unauthorized) - don't redirect on login/register pages
        if (error.response?.status === 401) {
          const currentPath = window.location.pathname
          if (currentPath !== '/login' && currentPath !== '/register') {
            localStorage.removeItem('access_token')
            window.location.href = '/login'
          }
        }
        
        // Attach original error for debugging
        ;(formattedError as any).originalError = error
        return Promise.reject(formattedError)
      }
    )
  }

  // Auth endpoints
  async login(credentials: LoginCredentials): Promise<Token> {
    try {
      // Use the JSON login endpoint
      const response = await this.client.post<Token>('/auth/login', credentials)
      return response.data
    } catch (error: any) {
      // Extract error message from response
      let errorMessage = 'Login fehlgeschlagen'
      if (error.response?.data?.detail) {
        errorMessage = typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : 'Ungültige Anmeldedaten'
      } else if (error.message) {
        errorMessage = formatErrorMessage(error)
      }
      throw new Error(errorMessage)
    }
  }

  async register(data: RegisterData): Promise<User> {
    try {
      const response = await this.client.post<User>('/auth/register', data)
      return response.data
    } catch (error: any) {
      // Re-throw with formatted error message
      throw new Error(formatErrorMessage(error))
    }
  }

  async getCurrentUser(): Promise<User> {
    const response = await this.client.get<User>('/auth/me')
    return response.data
  }

  async forgotPassword(data: PasswordResetRequest): Promise<PasswordResetResponse> {
    try {
      const response = await this.client.post<PasswordResetResponse>('/auth/forgot-password', data)
      return response.data
    } catch (error: any) {
      throw new Error(formatErrorMessage(error))
    }
  }

  async resetPassword(data: PasswordReset): Promise<{ message: string }> {
    try {
      const response = await this.client.post<{ message: string }>('/auth/reset-password', data)
      return response.data
    } catch (error: any) {
      throw new Error(formatErrorMessage(error))
    }
  }

  async deleteAccount(): Promise<void> {
    try {
      await this.client.delete('/auth/delete-account')
    } catch (error: any) {
      throw new Error(formatErrorMessage(error))
    }
  }

  // Audio endpoints
  async uploadAudio(
    file: File,
    onProgress?: (progress: number) => void,
    metadata?: {
      context?: string
      participants?: Array<{ name: string; gender?: Gender }>
    }
  ): Promise<Recording> {
    // Validierung und Logging
    if (!file) {
      console.error('uploadAudio: Kein File übergeben')
      throw new Error('Kein File zum Upload übergeben')
    }
    
    if (file.size === 0) {
      console.error('uploadAudio: File ist leer', { name: file.name, type: file.type })
      throw new Error('Die Datei ist leer')
    }
    
    console.log('uploadAudio: Starte Upload', {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      hasContext: !!metadata?.context,
      participantsCount: metadata?.participants?.length || 0
    })
    
    const formData = new FormData()
    formData.append('file', file)
    
    if (metadata?.context) {
      formData.append('context', metadata.context)
      console.log('uploadAudio: Context hinzugefügt:', metadata.context.substring(0, 50))
    }
    
    // Only send participants if they exist and have valid names
    if (metadata?.participants && metadata.participants.length > 0) {
      // Double-check: filter out any participants with empty names (defensive programming)
      const validParticipants = metadata.participants.filter(
        p => p.name && typeof p.name === 'string' && p.name.trim().length > 0
      )
      if (validParticipants.length > 0) {
        formData.append('participants', JSON.stringify(validParticipants))
        console.log('uploadAudio: Participants hinzugefügt:', validParticipants.length)
      }
    }

    // Don't set Content-Type header manually - let the browser set it with boundary
    // Axios will automatically set the correct Content-Type with boundary for FormData
    console.log('uploadAudio: Sende Request an /audio/upload')
    console.log('uploadAudio: FormData entries:', Array.from(formData.entries()).map(([k, v]) => [k, v instanceof File ? `File(${v.name}, ${v.size} bytes)` : v]))
    const response = await this.client.post<Recording>('/audio/upload', formData, {
      headers: {
        'Content-Type': undefined, // Wichtig: Entferne Content-Type, damit Browser multipart/form-data mit Boundary setzt
      },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total && onProgress) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          onProgress(progress)
        }
      },
    })
    console.log('uploadAudio: Upload erfolgreich', { recordingId: response.data.id })
    return response.data
  }

  async updateRecordingMetadata(
    id: string,
    metadata: {
      context?: string | null
      participants?: Array<{ name: string; gender?: Gender }> | null
    }
  ): Promise<Recording> {
    const response = await this.client.put<Recording>(`/audio/${id}/metadata`, metadata)
    return response.data
  }

  async getRecordingMetadata(id: string): Promise<Recording> {
    return this.getRecording(id)
  }

  async listRecordings(limit = 50, offset = 0): Promise<RecordingListResponse> {
    const response = await this.client.get<RecordingListResponse>('/audio/list', {
      params: { limit, offset },
    })
    return response.data
  }

  async getRecording(id: string): Promise<Recording> {
    const response = await this.client.get<Recording>(`/audio/${id}`)
    return response.data
  }

  getStreamUrl(id: string): string {
    const token = localStorage.getItem('access_token')
    if (token) {
      return `${API_URL}/api/v1/audio/stream/${id}?token=${encodeURIComponent(token)}`
    }
    return `${API_URL}/api/v1/audio/stream/${id}`
  }
}

export const apiClient = new ApiClient()

